import { processFewShotExamples } from '@/components/EvaluatorCrudPane/utils/fewShotExamplesUtils';
import { RunSchema } from '@/types/schema';

// Todo: clean this up (code gen logic). It's a mess from the gitea vue component.
const DEFAULT_LANG = 'py';
const formatLib = {
  py: (lib) => lib.join('.'),
  js: (lib) => lib.join('/'),
};
const formatImports = {
  py: (imports: Record<string, string[]>) =>
    Object.entries(imports)
      .map(([lib, classNames]) =>
        classNames
          .map((className) => `from ${lib} import ${className}`)
          .join('\n')
      )
      .join('\n'),
  js: (imports: Record<string, string[]>) =>
    Object.entries(imports)
      .map(([lib, classNames]) => {
        const libJs = lib.startsWith('langchain_')
          ? `@langchain/${lib.substring(10)}`
          : lib;
        return `import { ${classNames.join(', ')} } from "${libJs}";`;
      })
      .join('\n'),
};
const commentPrefix = {
  py: '# ',
  js: '// ',
};
const keywordArgs = {
  py: (key: string, value: string) => `${key}=${value}`,
  js: (key: string, value: string) => `${key}: ${value}`,
};
const paramPrefix = {
  py: '',
  js: '{',
};
const paramSuffix = {
  py: '',
  js: '}',
};
const constructorPrefix = {
  py: '',
  js: 'new ',
};
const varPrefix = {
  py: '',
  js: 'const ',
};
const fromMessages = {
  py: 'ChatPromptTemplate.from_messages',
  js: 'ChatPromptTemplate.fromMessages',
};
const fromTemplate = {
  py: 'PromptTemplate.from_template',
  js: 'PromptTemplate.fromTemplate',
};
const messageShorthandWrapperStart = {
  py: '(',
  js: '[',
};
const messageShorthandWrapperEnd = {
  py: ')',
  js: ']',
};

function idToMessageRole(id: string[]): string | null {
  const last = id[id.length - 1];
  // system, human, ai
  if (last.includes('System')) return 'system';
  if (last.includes('Human')) return 'human';
  if (last.includes('AI')) return 'ai';
  if (last === 'MessagesPlaceholder') return 'messages list';

  throw Error(`Unknown message role for id: ${id}`);
}

export function jsonToLang(json: any, lang: 'py' | 'js' = DEFAULT_LANG) {
  const context: { imports: Record<string, string[]>; secrets: string[] } = {
    imports: {}, // map library to list of objects
    secrets: [],
  };
  function innerCreate(obj) {
    if (Array.isArray(obj)) {
      const inner = obj.flatMap((item) => innerCreate(item));
      return ['[', ...inner.map((row) => `  ${row}`), '],'];
    }
    if (!(obj instanceof Object && 'type' in obj)) {
      // should only be primitives
      return [JSON.stringify(obj) + ','];
    }
    if (obj.type === 'constructor') {
      // first, try constructing shorthand for ChatPromptTemplate and PromptTemplate
      try {
        if (
          JSON.stringify(obj.id) ===
          JSON.stringify(['langchain', 'prompts', 'chat', 'ChatPromptTemplate'])
        ) {
          const messages = obj.kwargs.messages;

          const messageTuples = messages.map((message) => {
            const role = idToMessageRole(message.id);

            let content =
              role === 'messages list'
                ? `{${message.kwargs.variable_name}}`
                : message.kwargs.prompt.kwargs.template;

            // Process few-shot examples to show only the placeholder
            if (role !== 'messages list' && content) {
              const { processedMessages } = processFewShotExamples([
                ['temp', content],
              ]);
              content = processedMessages[0]?.[1] || content;
            }

            const libArr = ['langchain_core', 'prompts'];
            const lib = formatLib[lang](libArr);
            if (!(lib in context.imports)) {
              context.imports[lib] = [];
            }
            if (!context.imports[lib].includes('ChatPromptTemplate')) {
              context.imports[lib].push('ChatPromptTemplate');
            }
            return `  ${messageShorthandWrapperStart[lang]}"${role}", "${content}"${messageShorthandWrapperEnd[lang]},`;
          });

          return [`${fromMessages[lang]}([`, ...messageTuples, ']),'];
        }
        if (
          JSON.stringify(obj.id) ===
          JSON.stringify(['langchain', 'prompts', 'prompt', 'PromptTemplate'])
        ) {
          const template = JSON.stringify(obj.kwargs.template);
          const lib = formatLib[lang](['langchain_core', 'prompts']);
          if (!(lib in context.imports)) {
            context.imports[lib] = [];
          }
          if (!context.imports[lib].includes('ChatPromptTemplate')) {
            context.imports[lib].push('PromptTemplate');
          }
          return [`${fromTemplate[lang]}(${template}),`];
        }
      } catch (e) {
        // go back to old logic
        console.error(e);
      }

      // format import from id
      const libArr = [...obj.id];
      const className = libArr.pop();
      const lib = formatLib[lang](libArr);
      if (!(lib in context.imports)) {
        context.imports[lib] = [];
      }
      if (!context.imports[lib].includes(className)) {
        context.imports[lib].push(className);
      }
      // get all kwargs
      const constructorArgs = Object.entries(obj.kwargs).flatMap(
        ([key, value]) => {
          const valRows = innerCreate(value);
          if (valRows.length === 0) return [];
          if (valRows.length === 1)
            return [`  ${keywordArgs[lang](key, valRows[0])}`];
          if (valRows.length > 1) {
            const firstRow = `  ${keywordArgs[lang](key, valRows[0])}`;
            const rest = Array.from(valRows).slice(1);
            return [firstRow, ...rest.map((row) => `  ${row}`)];
          }
          return [];
        }
      );
      // return python line
      const first = `${constructorPrefix[lang]}${className}(${paramPrefix[lang]}`;
      if (constructorArgs.length === 0)
        return [`${first}${paramSuffix[lang]}),`];
      return [first, ...constructorArgs, `${paramSuffix[lang]}),`];
    } else if (obj.type === 'secret') {
      // add secret id to secrets
      for (const id of obj.id) {
        if (!context.secrets.includes(id)) {
          context.secrets.push(id);
        }
      }
      return [];
    }
    throw new Error(`Unimplemented type on ${JSON.stringify(obj)}`);
  }
  try {
    const lines = innerCreate(json);
    const secrets = context.secrets.length
      ? `${commentPrefix[lang]} Remember to include the following SECRETS:\n` +
        context.secrets.map((s) => `${commentPrefix[lang]} - ${s}`).join('\n') +
        '\n\n'
      : '';
    const imports = Object.keys(context.imports).length
      ? formatImports[lang](context.imports) + '\n\n'
      : '';
    const code = lines.join('\n');
    const rtn = `${secrets}${imports}${varPrefix[lang]}prompt = ${code}`;
    return rtn.substring(0, rtn.length - 1);
  } catch (e) {
    console.error(e);
  }
}

export function generatePullCode(
  owner: string | undefined,
  repo: string,
  commit?: string,
  isPublic?: boolean,
  lang: 'py' | 'js' = DEFAULT_LANG
) {
  const lastCommitHash = commit?.slice(0, 8) ?? null;
  const lastCommitHashStr = lastCommitHash ? `:${lastCommitHash}` : '';
  return lang === 'py'
    ? `# Create a LANGSMITH_API_KEY in Settings > API Keys
from langsmith import Client
client = Client(api_key=LANGSMITH_API_KEY)
prompt = client.pull_prompt("${
        owner && isPublic
          ? `${owner}/${repo}${lastCommitHashStr}`
          : `${repo}${lastCommitHashStr}`
      }", include_model=True)`
    : `// Set the LANGSMITH_API_KEY environment variable (create key in Settings > API Keys)

// If you are in a non-Node environment, please use the default "langchain/hub" entrypoint and omit includeModel for providers other than OpenAI
import * as hub from "langchain/hub/node";\n\n` +
        `await hub.pull("${
          owner && isPublic
            ? `${owner}/${repo}${lastCommitHashStr}`
            : `${repo}${lastCommitHashStr}`
        }", {\n  includeModel: true\n});`;
}

export function descriptionToOneLine(description: string) {
  return description.split('\n')[0];
}

export function getTenantHandleValidationError(
  handle: string,
  strict = false
): Error | null {
  if (!strict && handle.length === 0) return null;

  if (!/^[a-z][a-z0-9-]*$/.test(handle)) {
    return new Error(
      'Handle must start with a letter and only ' +
        'contain lowercase letters, numbers, and hyphens'
    );
  }
  return null;
}

export function getRepoHandleValidationError(
  handle: string,
  strict = false
): Error | null {
  if (!strict && handle.length === 0) return null;

  if (!/^[a-z][a-z0-9-_]*$/.test(handle)) {
    return new Error(
      'Prompt name must start with a letter and only ' +
        'contain lowercase letters, numbers, hyphens, or underscore'
    );
  }
  return null;
}

export function getNumberWithSuffix(num: number | undefined): string {
  if (num === undefined) {
    return '';
  }
  const suffixes = ['', 'k', 'M', 'B', 'T'];
  const maxNum = Math.pow(10, suffixes.length * 3) - 1;
  if (num > maxNum) {
    return num.toExponential(0);
  }

  const intNum = Math.floor(num);
  const orderMag = Math.floor(Math.log10(intNum || 1)); // don't take log(0)
  const suffixIndex = Math.min(Math.floor(orderMag / 3), suffixes.length - 1);
  const suffixDivisor = Math.pow(10, suffixIndex * 3);
  const suffix = suffixes[suffixIndex];
  const shortNum = intNum / suffixDivisor;
  const shortNumStr = shortNum
    .toFixed(2)
    .toString()
    .slice(0, 4)
    .replace(/\.0*$/, '');
  return `${shortNumStr}${suffix}`;
}

export function getHubInfoFromRun(node: RunSchema): {
  owner?: string;
  repo: string;
  commit?: string;
} | null {
  const metadata = node.extra?.metadata;
  const { lc_hub_owner, lc_hub_repo, lc_hub_commit_hash } = metadata ?? {};
  if (!lc_hub_repo) {
    return null;
  }
  return { owner: lc_hub_owner, repo: lc_hub_repo, commit: lc_hub_commit_hash };
}

export function getHubLinkFromSessionName(sessionName: string) {
  // extract from e.g. efriis/my-first-prompt:52668c2f 2024-03-29T00:02:59.250Z
  // want efriis/my-first-prompt/52668c2f
  const regex = /^([a-z][a-z0-9-_]*)\/([a-z][a-z0-9-_]*):([a-z0-9]{8})/;
  const match = sessionName.match(regex);
  if (!match) {
    return null;
  }
  const [, owner, repo, commit] = match;
  return { owner, repo, commit };
}
